import { useCallback, useContext } from 'react';
import { GrippContext } from '../grippContext';
import { useIsFeatureEnabled } from '../features';
import {
  FeatureName,
  cacheKeys,
  requestGraphQL,
  useCreateMutation,
  useCustomMutation,
} from '../graphql';
import { useCreateIssueWithMessage } from '../issues/hooks/useCreateIssueWithMessage';
import { CREATE_ASSET_ROUTINE } from '../routine/queries';
import { AUTHENTICATION_QUERY_KEY } from '../auth/constants';
import type { Authentication } from '../auth/domain';
import {
  ADD_ASSET,
  CREATE_REMINDER_WORKFLOW,
  CREATE_TAG_SCAN,
  GET_WORKFLOW_FOR_REPLAY,
} from './offlineMutations';
import { useQueryClient } from '@tanstack/react-query';
import {
  OutboxUnavailableError,
  type OfflineMediaRef,
  type OfflineSubmissions,
  type OfflineSubmitKind,
  type SubmitResult,
} from './outboxService';

export interface OfflineSubmitOptions {
  /**
   * Reject with `OutboxUnavailableError` rather than send directly when the
   * outbox cannot take the work, decided at call time: a direct request made
   * offline waits in memory and is lost if the app is killed.
   */
  queueOnly?: boolean;
}

/**
 * The seam the forms submit through. With the outbox injected (the app) and
 * `OFFLINE_WRITES` on, the work is queued and survives going offline or being
 * killed; otherwise it is sent directly exactly as before the outbox existed.
 */
export const useOfflineSubmit = () => {
  const outbox = useContext(GrippContext)?.outbox;
  const offlineWrites = useIsFeatureEnabled(FeatureName.OfflineWrites);
  const queryClient = useQueryClient();
  const { mutateAsync: createAssetRoutine } = useCreateMutation({
    query: CREATE_ASSET_ROUTINE,
    modelName: 'assetRoutine',
  });
  const { mutateAsync: reportIssue } = useCreateIssueWithMessage();
  const { mutateAsync: createWorkflow } = useCreateMutation({
    query: CREATE_REMINDER_WORKFLOW,
    modelName: 'workflow',
  });
  const { mutateAsync: addAsset } = useCustomMutation({ query: ADD_ASSET });
  // `mutate`, not `mutateAsync`: a scan has never held up, or failed, the
  // screen that records it.
  const { mutate: createTagScan } = useCreateMutation({
    query: CREATE_TAG_SCAN,
    modelName: 'tagScan',
  });

  /** True when this account already holds a reminder under the id; another account's is an error. */
  const reminderExists = useCallback(
    async (id: string): Promise<boolean> => {
      const { getWorkflow: found } = await requestGraphQL<{
        getWorkflow: { createdBy?: { keyCloakId?: string | null } | null } | null;
      }>(GET_WORKFLOW_FOR_REPLAY, { id });
      if (!found) return false;
      const caller = queryClient.getQueryData<Authentication>(
        AUTHENTICATION_QUERY_KEY
      )?.jwt?.sub;
      if (!caller || found.createdBy?.keyCloakId !== caller) {
        throw new Error(
          'A reminder with this id already belongs to another account.'
        );
      }
      return true;
    },
    [queryClient]
  );

  const sendDirectly = useCallback(
    async <K extends OfflineSubmitKind>(
      kind: K,
      payload: OfflineSubmissions[K]
    ): Promise<SubmitResult> => {
      switch (kind) {
        case 'createAssetRoutine': {
          const { input, media } =
            payload as OfflineSubmissions['createAssetRoutine'];
          // A photo captured to the device has no server record; sending the
          // routine on its own would reference ids that never upload.
          if (media?.length) throw new OutboxUnavailableError();
          await createAssetRoutine(
            { input },
            {
              onSuccess: async () => {
                await queryClient.invalidateQueries({
                  queryKey: cacheKeys('issue').all,
                  exact: false,
                });
              },
            }
          );
          return { status: 'accepted', rowId: null };
        }
        case 'reportIssue': {
          const { input, reminder, media } =
            payload as OfflineSubmissions['reportIssue'];
          if (media?.length) throw new OutboxUnavailableError();
          // The client id goes with the request so the id the form navigates
          // to is the one the server holds; capturedAt is not sent, since a
          // request made now has nothing earlier to preserve.
          const reported = await reportIssue({ input });
          if (reminder) {
            // The reminder waits for the report, as it does in the queue.
            const issueId: string = reported?.issue?.reportIssue?.id ?? input.id;
            try {
              // createWorkflow is insert-only and the form re-sends this id on
              // a retry, so a reminder that committed with its response lost
              // must be recognised, not created again.
              if (!(await reminderExists(reminder.id))) {
                await createWorkflow({
                  input: {
                    id: reminder.id,
                    ...reminder.input,
                    trigger: {
                      ...reminder.input.trigger,
                      config: { ...reminder.input.trigger.config, issueId },
                    },
                  },
                });
              }
            } catch (error) {
              // The report is saved. Rejecting here would strand roles that
              // may report but not create reminders on a form whose retry
              // can only fail the same way.
              console.error('Reminder not created after report:', error);
              return { status: 'accepted', rowId: null, reminderFailed: true };
            }
          }
          return { status: 'accepted', rowId: null };
        }
        case 'addAsset': {
          const { input, media } = payload as OfflineSubmissions['addAsset'];
          if (media?.length) throw new OutboxUnavailableError();
          await addAsset({ input });
          // Not awaited, as the asset form does today.
          queryClient
            .invalidateQueries({ queryKey: cacheKeys('asset').all })
            .catch(() => undefined);
          return { status: 'accepted', rowId: null };
        }
        case 'tagScan': {
          const { id, tagId, capturedAt, location } =
            payload as OfflineSubmissions['tagScan'];
          createTagScan({
            input: {
              id,
              tag: { id: tagId },
              location,
              createdAt: new Date(capturedAt).toISOString(),
            },
          });
          return { status: 'accepted', rowId: null };
        }
      }
    },
    [
      createAssetRoutine,
      createTagScan,
      reportIssue,
      createWorkflow,
      addAsset,
      queryClient,
      reminderExists,
    ]
  );

  const submit = useCallback(
    async <K extends OfflineSubmitKind>(
      kind: K,
      payload: OfflineSubmissions[K],
      options: OfflineSubmitOptions = {}
    ): Promise<SubmitResult> => {
      if (outbox && offlineWrites) {
        try {
          return await outbox.submit(kind, payload);
        } catch (error) {
          if (!(error instanceof OutboxUnavailableError)) throw error;
        }
      }
      if (options.queueOnly) throw new OutboxUnavailableError();
      return sendDirectly(kind, payload);
    },
    [outbox, offlineWrites, sendDirectly]
  );

  // There is no direct path for local photos: without the outbox nothing
  // would ever upload them, so the caller must not go on to reference them.
  const submitMedia = useCallback(
    async (media: OfflineMediaRef[], workspaceId: string): Promise<void> => {
      if (media.length === 0) return;
      if (!outbox || !offlineWrites) throw new OutboxUnavailableError();
      await outbox.submitMedia(media, workspaceId);
    },
    [outbox, offlineWrites]
  );

  // Read at render: the form decides whether to capture photos to the device
  // from this, and `submit` still falls back if the outbox goes away after.
  const canQueue = !!outbox && offlineWrites && outbox.canAccept();

  return { submit, submitMedia, canQueue };
};
